import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Note: destructive hover uses the literal #b8112f — the DS hardcodes this
// (no --ollio-danger-hover token upstream); do NOT "tokenise" it, that would
// diverge from the DS and trip the vendored-token drift guard.
// Re-authored from ../design-system/components/buttons/Button.jsx (ADR-032:
// match visual output, not the prototype's injected-CSS/UMD code).
// buttonVariants is exported so consumers (e.g. GuestNav) can style Links
// as gold pill buttons without nesting a <button> inside <a>.
// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap border border-transparent font-semibold ' +
    'transition disabled:opacity-50 disabled:cursor-not-allowed ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'focus-visible:ring-offset-[var(--bg-page)]',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground shadow-[var(--shadow-sm)] ' +
          'enabled:hover:bg-[var(--primary-hover)] enabled:hover:-translate-y-0.5 enabled:hover:shadow-[var(--shadow-md)] ' +
          'enabled:active:bg-[var(--primary-active)] enabled:active:translate-y-0 enabled:active:shadow-[var(--shadow-xs)]',
        gold:
          'bg-accent text-accent-foreground shadow-[var(--shadow-gold)] ' +
          'enabled:hover:bg-[var(--accent-hover)] enabled:hover:-translate-y-0.5 ' +
          'enabled:active:bg-[var(--ollio-gold-deep)] enabled:active:translate-y-0 enabled:active:shadow-[var(--shadow-xs)]',
        secondary:
          'bg-card text-primary border-border ' +
          'enabled:hover:bg-[var(--ollio-navy-light)] enabled:hover:border-primary ' +
          'enabled:active:bg-[var(--ollio-cream-200)]',
        ghost:
          'bg-transparent text-primary ' +
          'enabled:hover:bg-[var(--ollio-navy-light)] enabled:active:bg-[var(--ollio-cream-200)]',
        destructive:
          'bg-destructive text-destructive-foreground ' +
          'enabled:hover:bg-[#b8112f] enabled:hover:-translate-y-0.5 enabled:active:translate-y-0',
      },
      size: {
        sm: 'h-8 px-4 text-sm',
        md: 'h-10 px-6 text-base',
        lg: 'h-12 px-8 text-[1.125rem]',
      },
      pill: { true: 'rounded-full', false: 'rounded-[var(--radius-control)]' },
    },
    defaultVariants: { variant: 'primary', size: 'md', pill: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, pill, loading = false, leadingIcon, trailingIcon, disabled, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, pill }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-disabled={disabled || loading || undefined}
      {...rest}
    >
      {loading && <Loader2 className="h-[1em] w-[1em] animate-spin" aria-hidden="true" />}
      {!loading && leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});
